'use client';

import { memo, useCallback, useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import { MenuBodyProps } from './types';
import Backdrop from '@/components/Backdrop';
import HeaderContext, { HeaderContextType } from '@/context/HeaderContext';
import config from 'config-base';
import Link from '@/components/Link';
import SubCategorie from '../SubCategorie';
import useMediaQuery from '@/hooks/useMediaQuery';
import { MenuItem } from '../types';

import styles from './MenuBody.module.css';

const MenuBody = ({ menuData }: MenuBodyProps) => {
  const {
    height: topPosition,
    isMenuOpened,
    isCartOpened,
    setIsMenuOpened,
  } = useContext(HeaderContext) as HeaderContextType;
  const mobile: boolean = useMediaQuery('(max-width:1279px)');

  const [activeSubCategories, setActiveSubCategories] = useState<MenuItem | null>(null);

  const handleSetActiveMenu = (event: React.MouseEvent<HTMLElement>, id: number) => {
    event.stopPropagation();
    const subMenu = (menuData && menuData?.find((menuItem) => menuItem.id === id)) || null;
    if (subMenu) {
      setActiveSubCategories(subMenu as unknown as MenuItem);
    }
  };

  const handleSetOnmouseActiveMenu = (event: React.MouseEvent<HTMLElement>, id: number) => {
    if (mobile) return;

    event.stopPropagation();
    const subMenu = menuData?.find((menuItem) => menuItem.id === id) || null;
    if (subMenu) {
      setActiveSubCategories(subMenu as unknown as MenuItem);
    }
  };

  const handleMenuBack = useCallback(() => {
    setTimeout(() => {
      setActiveSubCategories(null);
    }, 500);
  }, []);

  const closeMenu = useCallback(() => {
    setActiveSubCategories(null);
    setIsMenuOpened(false);
  }, []);

  useEffect(() => {
    return () => {
      setActiveSubCategories(null);
    };
  }, []);

  useEffect(() => {
    if (document) {
      setTimeout(() => {
        const htmlRoot = document?.querySelector('html');
        if (isMenuOpened) {
          if (mobile && window.scrollY < topPosition) {
            window.scrollTo({ top: 0 });
          }
          htmlRoot?.classList.add('preventScroll');
        } else if (!isCartOpened) {
          htmlRoot?.classList.remove('preventScroll');
        }
      }, 0);
    }
  }, [isMenuOpened]);

  return (
    <aside id="menu-container" data-testid="menu-container">
      <nav
        style={{ top: `${topPosition}px` }}
        className={clsx(styles.menuBody, {
          [styles.menuBodySlideLeftAnimation]: isMenuOpened,
        })}
        onMouseEnter={() => setIsMenuOpened(true)}
        onMouseLeave={closeMenu}
        id="pyrMenu"
        data-testid={'pyr-menu-nav'}
      >
        <ul className={`${styles.menuBodyList} pyr-menu-content`}>
          {menuData?.map((menu: MenuItem) => (
            <li
              onMouseEnter={(el) => handleSetOnmouseActiveMenu(el, menu.id)}
              onClick={(el) => handleSetActiveMenu(el, menu.id)}
              key={menu.id}
              className={`${styles.menuBodyListItem} icon-right`}
              id={`mmCat_${menu.id}`}
              data-testid={`menu-content-${menu.id}`}
            >
              {mobile && menu.sub && menu.sub?.length > 0 ? (
                <p className={styles.menuBodyName}>{menu.name}</p>
              ) : (
                <Link data-testid={`menu-content-link-${menu.id}`} href={`${menu.url}`} classes={styles.menuBodyName} showCursorPointer>
                  {menu.name}
                </Link>
              )}

              {menu.sub && menu.sub?.length > 0 && mobile && <span className={styles.menuBodyArrowIcon} />}
              {menu.sub && menu.sub?.length > 0 ? (
                <SubCategorie
                  mainCategory={{ name: menu.name, url: menu.url }}
                  subCategorie={menu}
                  handleMenuBack={handleMenuBack}
                  active={menu.id === activeSubCategories?.id}
                  mainUrl={`${config.mainPyramydairUrl}`}
                  mobile={mobile}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </nav>
      {mobile && isMenuOpened && (
        <Backdrop style={{ top: `${topPosition}px` }} className={styles.menuBodyBackdrop} onClick={closeMenu} />
      )}
    </aside>
  );
};

export default memo(MenuBody);
